import { ThemeTypings } from '@chakra-ui/react';
import { Status } from '@main/complykit/components/status';

import { Combobox, ComboboxProps, withMultiCombobox } from '../combobox';
import { StatusTone, toneFromColorScheme } from '../legacy-color-scheme';

export type StatusOption = {
  value: string;
  label?: string;
  /** Semantic tone (badge-status-tag.md). Preferred over `colorScheme`. */
  tone?: StatusTone;
  /** Legacy Chakra scheme; bridged onto the five tones when no tone is given. */
  colorScheme?: ThemeTypings['colorSchemes'];
};

export const defaultProps = {
  getOptionValue: (option) => option.value,
  getOptionLabel: (option) => option.label || option.value,

  components: {
    Option: (props) => {
      const label = defaultProps.getOptionLabel(props);
      const tone = props.tone ?? toneFromColorScheme(props.colorScheme);

      if (!props.removeProps) {
        return <Status tone={tone}>{label}</Status>;
      }

      /* react-select's removeProps adapt onto Status's native remove exactly
       * as EditableTag does with Tag: onClick is removeValue and ignores its
       * event (so it becomes the plain onRemove callback), onTouchEnd is
       * covered by the RAC Button's press handling, and onMouseDown's
       * preventDefault (keep the combobox input focused) by the button's
       * preventFocusOnPress. The slot is legal HERE because a combobox seat
       * is a removable SELECTION of a status (badge-status-tag.md);
       * Status-as-state never renders it. */
      return (
        <Status
          tone={tone}
          onRemove={() => (props.removeProps?.onClick as (() => void) | undefined)?.()}
          removeLabel={`Remove ${label}`}
        >
          {label}
        </Status>
      );
    },
  },
} satisfies ComboboxProps<StatusOption>;

export function EditableStatus<TOption extends StatusOption, IsMulti extends boolean = false>(
  props: Omit<ComboboxProps<TOption, IsMulti>, 'components'>,
) {
  return <Combobox {...defaultProps} {...withMultiCombobox(props)} />;
}

EditableStatus.Option = defaultProps.components.Option;
